import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router, ActivationEnd, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Subscription, catchError, firstValueFrom } from 'rxjs';

import { AUTH_SERVER_URL, BUILD_NUMBER } from '../../config';
import { User } from '../models/user';

const ANALYTICS_URL = `${ AUTH_SERVER_URL }/v1/analytics/event`;

interface AnalyticsEventPayload {
	eventType: 'PAGE_VIEW' | 'ACTION';
	actionName?: string | null;
	pageName?: string | null;
	pageCategory?: string | null;
	previousPage?: string | null;
	browser?: string | null;
	deviceType?: string | null;
	resultCount?: number | null;
	applicationVersion?: string;
	sessionId?: string | null;
	jsonData?: string | null;
}

@Injectable({ providedIn: 'root' })
export class DbAnalyticsService implements OnDestroy {
	private readonly sessionId: string;

	private lastPageName = '';
	private lastPageCategory = '';
	private currentPageName = '';
	private currentPageCategory = '';
	private currentPageParams: Record<string, string> | null = null;

	private readonly routerSub: Subscription;

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly ngZone: NgZone,
	) {
		// Generate a per-tab session UUID stored in sessionStorage
		const stored = sessionStorage.getItem('copo-analytics-session-id');
		if (stored) {
			this.sessionId = stored;
		} else {
			this.sessionId = crypto.randomUUID();
			sessionStorage.setItem('copo-analytics-session-id', this.sessionId);
		}

		this.routerSub = this.router.events.subscribe(ev => {
			if (ev instanceof ActivationEnd && !ev.snapshot.firstChild) {
				// Leaf route — capture page metadata
				const pageName: string = ev.snapshot.data['pageName'] ? String(ev.snapshot.data['pageName']) : '';
				// Walk up route tree to find the deepest ancestor with a 'service' set
				const pageCategory: string = ev.snapshot.pathFromRoot
					.filter(s => Boolean(s.data['service']))
					.map(s => String(s.data['service']))
					.pop() ?? '';
				this.currentPageName = pageName;
				this.currentPageCategory = pageCategory;
				// Card pages carry their key identifiers (id, reference, etc.) as route params
				this.currentPageParams = pageName.endsWith('Card') && Object.keys(ev.snapshot.params).length > 0
					? { ...ev.snapshot.params }
					: null;
			} else if (ev instanceof NavigationEnd) {
				// Skip redirect pages
				if (!this.currentPageName || this.currentPageName === 'Redirect') {
					this.currentPageName = '';
					return;
				}

				this.sendPageView();
			}
		});
	}

	setUserContext(_user?: User) {
		// Identity is extracted server-side from the OAuth2 token; kept for API compatibility.
	}

	trackAction(actionName: string, jsonData?: object | null, resultCount?: number | null) {
		const payload: AnalyticsEventPayload = {
			eventType: 'ACTION',
			actionName,
			pageName: this.lastPageName || null,
			pageCategory: this.lastPageCategory || null,
			resultCount: resultCount ?? null,
			applicationVersion: BUILD_NUMBER,
			sessionId: this.sessionId,
			jsonData: jsonData ? JSON.stringify(jsonData) : null,
			browser: this.detectBrowser(),
			deviceType: this.detectDeviceType(),
		};
		this.ngZone.runOutsideAngular(() => {
			this.post(payload).catch(() => undefined);
		});
	}

	ngOnDestroy() {
		this.routerSub.unsubscribe();
	}

	/** Sent unconditionally at NavigationEnd - not correlated with the page's own data loading. */
	private sendPageView() {
		const pageName = this.currentPageName;
		const pageCategory = this.currentPageCategory;
		const previousPage = this.lastPageName || null;
		const pageParams = this.currentPageParams;

		this.lastPageName = pageName;
		this.lastPageCategory = pageCategory;
		this.currentPageName = '';
		this.currentPageParams = null;

		const payload: AnalyticsEventPayload = {
			eventType: 'PAGE_VIEW',
			pageName,
			pageCategory: pageCategory || null,
			previousPage,
			applicationVersion: BUILD_NUMBER,
			sessionId: this.sessionId,
			jsonData: pageParams ? JSON.stringify(pageParams) : null,
			browser: this.detectBrowser(),
			deviceType: this.detectDeviceType(),
		};
		this.ngZone.runOutsideAngular(() => {
			this.post(payload).catch(() => undefined);
		});
	}

	private async post(payload: AnalyticsEventPayload): Promise<void> {
		await firstValueFrom(
			this.http.post(ANALYTICS_URL, payload).pipe(catchError(() => EMPTY)),
			{ defaultValue: null },
		);
	}

	private detectBrowser(): string {
		const ua = navigator.userAgent;
		if (ua.includes('Edg/')) {
			return 'Edge';
		}
		if (ua.includes('Chrome/')) {
			return 'Chrome';
		}
		if (ua.includes('Firefox/')) {
			return 'Firefox';
		}
		if (ua.includes('Safari/')) {
			return 'Safari';
		}
		return 'Other';
	}

	private detectDeviceType(): string {
		if (/mobi|android/i.test(navigator.userAgent)) {
			return 'Mobile';
		}
		if (/tablet|ipad/i.test(navigator.userAgent) || (screen.width >= 768 && screen.width < 1024)) {
			return 'Tablet';
		}
		return 'Desktop';
	}
}
